import type { FormContext } from 'vee-validate'
import { computed, defineEmits, nextTick, ref } from 'vue'
import { checkCompanyDomainUnique, checkCompanyPrefixUnique, createCompanies } from '@/service/companies'
import { useCompanyStore } from '@/stores/companies'
import { eventBus } from '@/utils/utils'
import type { Company } from '@/views/companies/data/schema'

export function useCreateCompany(form?: FormContext) {
  const isLoading = ref(false)
  const isSubmitting = ref(false)
  const confirmDuplicate = ref(false)
  const showDuplicateDialog = ref(false)
  const pendingValues = ref<Record<string, any> | null>(null)

  // --- Validation State ---
  const prefixAsyncError = ref('')
  const domainAsyncError = ref('')
  const prefixChecking = ref(false)
  const domainChecking = ref(false)
  const backendError = ref('')
  const duplicateDomains = ref<string[]>([])

  const companyStore = useCompanyStore()
  const emit = defineEmits(['closeDialog'])
  const { setFieldError } = form ?? { setFieldError: () => undefined }

  // Normalize utility to trim and lowercase strings
  const normalize = (str: string | undefined): string => str?.trim().toLowerCase() || ''

  // --- Custom Async Validators ---
  async function validatePrefixUnique(value: string) {
    if (!value) return true
    prefixChecking.value = true
    prefixAsyncError.value = ''
    try {
      const { onSuccess, onError } = checkCompanyPrefixUnique(value)

      return await new Promise((resolve) => {
        onSuccess((data: any) => {
          prefixChecking.value = false
          if (data.exists) {
            prefixAsyncError.value = 'Prefix already exists. Please choose a different one.'
            resolve(false)
          } else {
            resolve(true)
          }
        })
        onError(() => {
          prefixChecking.value = false
          prefixAsyncError.value = 'Error checking prefix uniqueness.'
          resolve(false)
        })
      })
    } finally {
      prefixChecking.value = false
    }
  }

  async function validateDomainUnique(value: string) {
    if (!value) return true
    domainChecking.value = true
    domainAsyncError.value = ''
    try {
      const { onSuccess, onError } = checkCompanyDomainUnique(value)

      return await new Promise((resolve) => {
        onSuccess((data: any) => {
          domainChecking.value = false
          if (data.exists) {
            domainAsyncError.value = 'This domain is already taken. Please choose another.'
            resolve(false)
          } else {
            resolve(true)
          }
        })
        onError(() => {
          domainChecking.value = false
          domainAsyncError.value = 'Error checking domain uniqueness.'
          resolve(false)
        })
      })
    } finally {
      domainChecking.value = false
    }
  }

  const DOMAIN_REGEX: RegExp = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/

  const isFormDisabled = computed(() => {
    return isLoading.value || prefixChecking.value || domainChecking.value
  })

  // Handlers for duplicate name confirmation dialog
  function handleConfirmDuplicate() {
    confirmDuplicate.value = true
    showDuplicateDialog.value = false
    if (pendingValues.value) {
      onSubmit(pendingValues.value)
      pendingValues.value = null
    }
  }

  function handleCancelDuplicate() {
    showDuplicateDialog.value = false
    pendingValues.value = null
    isSubmitting.value = false
  }

  const errorMessage = computed(() => {
    if (!isSubmitting.value) {
      return ''
    }
    if (form?.errors.value && Object.keys(form.errors.value).length > 0) {
      const errors = Object.values(form.errors.value)
      const requiredErrors = errors.filter((error) => typeof error === 'string' && error.includes('required'))

      if (requiredErrors.length > 0) {
        return 'Please fill in all the required fields to create the company'
      }

      return typeof errors[0] === 'string' ? errors[0] : ''
    }

    return ''
  })

  // Handle Form Submission
  const onSubmit = async (values: Record<string, any>) => {
    // Check for duplicate company name and trigger confirmation dialog
    if (!confirmDuplicate.value) {
      const newName = normalize(values.company)
      const duplicateExists = companyStore.companies.some((c) => normalize(c.name) === newName)

      if (duplicateExists) {
        pendingValues.value = values
        showDuplicateDialog.value = true

        return
      }
    }
    isLoading.value = true
    backendError.value = ''
    prefixAsyncError.value = ''
    domainAsyncError.value = ''
    form?.setErrors({})

    duplicateDomains.value = []
    let handled = false
    const { onSuccess, onError } = createCompanies({
      name: values.company,
      description: ' ',
      prefix: values.prefix,
      address: values.address,
      domains: values.domains,
      phone: values.phone?.number ? values.phone.number : '',
    })

    onSuccess(async (data: any) => {
      // Map backend response to Company type, providing defaults for missing fields
      const company: Company = {
        id: data.id,
        name: data.name,
        description: data.description,
        address: data.address,
        phone: data.phone,
        prefix: data.prefix,
        mainContact: data.mainContact ?? {
          id: '',
          name: '',
          email: '',
          phone: '',
          avatar: '',
          customData: {},
          identities: {},
          lastSignInAt: BigInt(0),
          createdAt: BigInt(0),
          updatedAt: BigInt(0),
          applicationId: '',
          isSuspended: false,
          hasPassword: false,
          profile: {},
        },
        mainContactId: data.mainContactId,
        domains: data.domains ?? [],
        meta: data.meta ?? { totalMembers: 1 },
        branding: data.branding,
      }

      companyStore.companies.unshift(company)
      await nextTick()
      emit('closeDialog')
      eventBus.emit('openDrawerForCompany', company)
      isLoading.value = false
      handled = true
    })
    onError((err: any) => {
      isLoading.value = false
      handled = true
      let specificErrorHandled = false

      // Clear async validator errors as we are now relying on submission response
      prefixAsyncError.value = ''
      domainAsyncError.value = ''

      const errorMessageString = err?.message // The message is directly on the error object

      if (errorMessageString && typeof errorMessageString === 'string') {
        const msg = errorMessageString.toLowerCase()

        // Check for prefix error
        if (
          msg.includes('prefix') &&
          (msg.includes('unique') || msg.includes('already exist') || msg.includes('already in use'))
        ) {
          setFieldError('prefix', errorMessageString)
          specificErrorHandled = true
        }
        // Check for domain error
        else if (
          msg.includes('domain') &&
          (msg.includes('unique') ||
            msg.includes('already exist') ||
            msg.includes('already in use') ||
            msg.includes('registered'))
        ) {
          let specificDomainMsg = errorMessageString // Use original casing
          // Attempt to improve message if specific domains are mentioned (though current log doesn't show this structure)
          const domainMatch =
            /domain(?:s)? (?:is|are|is already|are already) (?:in use|registered):? (.+?)(?:\.|is already in use|is already registered)?$/i.exec(
              errorMessageString,
            )

          if (domainMatch?.[1]) {
            const problematicDomains = domainMatch[1]
              .split(',')
              .map((d: string) => d.trim())
              .join(', ')

            specificDomainMsg = `The following domain(s) are already in use: ${problematicDomains}. Please choose others.`
            duplicateDomains.value = domainMatch[1].split(',').map((d: string) => d.trim())
          } else {
            const singleDomainMatch = /domain '([^']*)'/i.exec(errorMessageString)

            if (singleDomainMatch?.[1]) {
              duplicateDomains.value = [singleDomainMatch[1]]
            }
          }
          setFieldError('domains', specificDomainMsg)
          specificErrorHandled = true
        }
      }

      // Only set generic backendError if no specific field error was handled
      if (!specificErrorHandled) {
        if (errorMessageString && typeof errorMessageString === 'string') {
          backendError.value = errorMessageString
        } else {
          backendError.value = 'An unknown error occurred. Please try again.'
        }
      } else {
        // If a specific field error was handled, ensure backendError is cleared
        backendError.value = ''
      }

      // If backendError (generic alert) was set, make it auto-dismiss
      if (backendError.value && !specificErrorHandled) {
        // Only apply timeout to generic error, not if specific was handled
        const currentBackendErrorMessage = backendError.value

        setTimeout(() => {
          if (backendError.value === currentBackendErrorMessage) {
            backendError.value = ''
          }
        }, 6000)
      }
    })
    setTimeout(() => {
      if (!handled) isLoading.value = false
    }, 5000)
  }

  return {
    validatePrefixUnique,
    validateDomainUnique,
    duplicateDomains,
    DOMAIN_REGEX,
    isFormDisabled,
    isLoading,
    isSubmitting,
    showDuplicateDialog,
    handleConfirmDuplicate,
    handleCancelDuplicate,
    errorMessage,
    backendError,
    onSubmit,
  }
}
